/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment form utilities
 *
 * @file assignmentFormUtils.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { DateTime } from 'luxon';
import Utils from '../../common/utils';
import {
  formatDateToStorage,
  parseDate,
  toFormDueDate,
  wallClockToBrowserLocal
} from '../../common/dateTimeUtils';
import { TIMEFIELD_CONSTANTS } from '../../common/constants';
import { resolveDefaultTimeZone } from '../QuickSchedule/quickScheduleFormUtils';
import { EMPTY_ASSIGNMENT_FORM_VALUES } from './assignmentFormActions';

const SECTION_NUMBER_PATTERN = /^\d+(?:\.\d+)*$/;

function titleMatchesSectionNumber(title, sectionNumber) {
  const normalizedSection = String(sectionNumber).trim();
  const normalizedTitle = String(title).trim();

  if (!normalizedSection || !normalizedTitle) {
    return false;
  }

  return normalizedTitle.startsWith(`${normalizedSection}:`)
    || normalizedTitle.startsWith(`${normalizedSection} `)
    || normalizedTitle === normalizedSection;
}

function findNodesBySectionNumbers(nodes, sectionNumbers, matched = []) {
  if (!Array.isArray(nodes)) {
    return matched;
  }

  nodes.forEach((node) => {
    if (!node?.id) {
      return;
    }

    const isMatch = sectionNumbers.some(
      (sectionNumber) => titleMatchesSectionNumber(node.title, sectionNumber)
    );

    if (isMatch) {
      matched.push(node);
    }

    if (node.children?.length) {
      findNodesBySectionNumbers(node.children, sectionNumbers, matched);
    }
  });

  return matched;
}

export function rehydrateChaptersFromContentCovered(contentCovered = [], tocData = []) {
  const sectionNumbers = contentCovered
    .map((item) => item?.sectionNumber)
    .filter((section) => section && SECTION_NUMBER_PATTERN.test(String(section)));

  if (!sectionNumbers.length || !tocData.length) {
    return [];
  }

  return findNodesBySectionNumbers(tocData, sectionNumbers);
}

function getAssignedNodesFromAssignment(assignment, tocData = []) {
  if (!assignment || !tocData.length) {
    return [];
  }

  if (assignment.textbookCoverage) {
    const hasStoredPages = Array.isArray(assignment.textbookCoverage?.pages)
      && assignment.textbookCoverage.pages.length > 0;

    return Utils.rehydrateSelectedNodesFromCoverage(
      assignment.textbookCoverage,
      tocData,
      { preferPages: hasStoredPages }
    );
  }

  if (assignment.contentCovered?.length) {
    return rehydrateChaptersFromContentCovered(assignment.contentCovered, tocData);
  }

  return [];
}

export function normalizeAssignmentDueDate(assignment) {
  const normalized = toFormDueDate(
    assignment?.date || assignment?.dueDate || assignment?.dateDue
  );

  return normalized || null;
}

/** Calendar dates (yyyy-MM-dd) already assigned to existing assignments. */
export function getOccupiedAssignmentDueDates(existingAssignments = []) {
  if (!Array.isArray(existingAssignments)) {
    return new Set();
  }

  return new Set(
    existingAssignments
      .map(normalizeAssignmentDueDate)
      .filter(Boolean)
  );
}

/** react-datepicker filterDate callback for assignment form CREATE mode. */
export function createAssignmentDueDateFilter(occupiedDates) {
  return (date) => {
    const dateIso = formatDateToStorage(date);

    return !occupiedDates.has(dateIso);
  };
}

export function buildRestrictedTocData(
  tocData,
  existingAssignments = [],
  { restrictAssigned = true, excludeAssignmentIndex = null } = {}
) {
  if (!restrictAssigned || !Array.isArray(tocData) || tocData.length === 0) {
    return tocData;
  }

  const assignmentsForRestriction = Number.isInteger(excludeAssignmentIndex)
    ? existingAssignments.filter((_, index) => index !== excludeAssignmentIndex)
    : existingAssignments;

  const assignedSectionIds = assignmentsForRestriction
    .flatMap((assignment) => getAssignedNodesFromAssignment(assignment, tocData)
      .map((node) => node.id)
      .filter(Boolean));

  return Utils.markAssignedSections(tocData, assignedSectionIds);
}

export function buildContentPickerSummary(chapters, tocData, chapterMcqMapping) {
  const coverageIds = Utils.getCoveragePageIdsForPayload(chapters, tocData);
  const selectedTitles = Utils.resolveDisplayTitles(coverageIds, tocData, true);
  const sectionNumbers = Utils.getAssignmentChaptersNumbers(
    selectedTitles.map(({ title }) => title)
  ) || [];
  const { sections, questions } = Utils.calculateSectionsAndQuestions(
    { chapters },
    chapterMcqMapping,
    tocData
  );

  const chaptersWithSelection = new Set(
    chapters
      .map((chapter) => Utils.getTopLevelChapterId(chapter, tocData))
      .filter(Boolean)
  ).size;

  const topLevelChapterIds = new Set(
    tocData
      .filter((chapter) => typeof chapter === 'object' && chapter?.id)
      .map((chapter) => chapter.id)
  );
  const showSectionsSummary = selectedTitles.some(({ id }) => !topLevelChapterIds.has(id));

  return {
    selectedTitles,
    sectionNumbers,
    sections,
    questions,
    chaptersWithSelection,
    showSectionsSummary
  };
}

export function buildFormDefaultValues(initialValues = {}, timeZoneOptions = []) {
  const { timeZone, ...restInitialValues } = initialValues;

  return {
    ...EMPTY_ASSIGNMENT_FORM_VALUES,
    ...restInitialValues,
    // Use initialValues.timeZone when parent provides it; otherwise browser match.
    timeZone: resolveDefaultTimeZone(timeZone, timeZoneOptions)
  };
}

export function mapCourseAssignmentToFormInitialValues(
  assignment,
  tocData = [],
  timeZoneOptions = []
) {
  if (!assignment) {
    return buildFormDefaultValues({}, timeZoneOptions);
  }

  const dueDateRaw = assignment.dueDate || assignment.dateDue || assignment.date;
  let dueDate = '';
  let dueTime = TIMEFIELD_CONSTANTS.DEFAULT_FORM_TIME;

  if (dueDateRaw) {
    const dueDateString = String(dueDateRaw);

    if (dueDateString.includes('T')) {
      const parsedDateTime = DateTime.fromISO(dueDateString);

      if (parsedDateTime.isValid) {
        dueDate = parsedDateTime.toFormat('yyyy-MM-dd');
        dueTime = parsedDateTime.toFormat('HH:mm');
      }
    } else {
      dueDate = dueDateString.slice(0, 10);
      dueTime = assignment.time || dueTime;
    }
  }

  const browserLocal = wallClockToBrowserLocal(
    assignment.date || dueDate,
    assignment.time,
    assignment.timeZone
  );

  // Convert stored wall-clock date/time to the browser's local display for the form.
  if (browserLocal) {
    dueDate = browserLocal.date || dueDate;
    dueTime = browserLocal.time || dueTime;
  }

  const chapters = getAssignedNodesFromAssignment(assignment, tocData);

  return buildFormDefaultValues({
    dueDate,
    dueTime,
    timeZone: assignment.timeZone || '',
    chapters,
    assignmentName: assignment.title || assignment.assignmentName || ''
  }, timeZoneOptions);
}

export function applyCourseAssignmentFormValues(assignment, values, tocData = []) {
  const chapters = values.chapters || [];
  let dueDate = assignment.dueDate || assignment.dateDue || assignment.date;

  if (values.dueDate) {
    if (values.dueTime) {
      const parsedDateTime = DateTime.fromISO(`${values.dueDate}T${values.dueTime}`);
      dueDate = parsedDateTime.isValid ? parsedDateTime.toISO() : values.dueDate;
    } else {
      dueDate = values.dueDate;
    }
  }

  const nextAssignment = {
    ...assignment,
    dueDate,
    date: values.dueDate || assignment.date,
    time: values.dueTime || assignment.time,
    timeZone: values.timeZone || assignment.timeZone,
    title: values.assignmentName || assignment.title
  };

  if (chapters.length > 0 && tocData.length > 0) {
    nextAssignment.textbookCoverage = Utils.createTextbookCoverage(chapters, tocData);
  }

  return nextAssignment;
}

export function getDateBadgeParts(dateString) {
  const date = parseDate(dateString);

  if (!date) {
    return {
      weekday: '',
      month: '',
      day: ''
    };
  }

  return {
    weekday: date.toLocaleString('en-US', { weekday: 'short' }).toUpperCase(),
    month: date.toLocaleString('en-US', { month: 'short' }),
    day: String(date.getDate())
  };
}
